import { Field, Dropdown, TextInput } from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { createYesNoOptions } from '@/config/formOptions';

// A declaration's GHG emission detail block — the RED III default-value toggle and its full breakdown.

const defaultValueOptions = createYesNoOptions({ yes: true, no: false });

const ESCA_BONUS_CAP_OPTION_KEYS = [
  ['NOT_CLAIMED', 'ESCA_BONUS_CAP_NOT_CLAIMED'],
  ['MANURE_BONUS', 'ESCA_BONUS_CAP_MANURE_BONUS'],
  ['NO_CAP_BEFORE_2013', 'ESCA_BONUS_CAP_NO_CAP_BEFORE_2013'],
  ['BIOFUEL_AFTER_2023', 'ESCA_BONUS_CAP_BIOFUEL_AFTER_2023'],
  ['BIOCHAR_USE', 'ESCA_BONUS_CAP_BIOCHAR_USE'],
  ['PRIOR_ESCA_CLAIMS', 'ESCA_BONUS_CAP_PRIOR_ESCA_CLAIMS'],
];

export const buildEscaBonusCapOptions = (t) =>
  ESCA_BONUS_CAP_OPTION_KEYS.map(([value, labelKey]) => ({ value, label: t(labelKey) }));

/**
 * Props: (none)
 */
export const DefaultGhgValueAppliedField = () => {
  const t = useTranslations();

  return (
    <Field name="defaultGhgValueApplied" required>
      {({ id, name, value, onChange, onBlur, status, message, required }) => (
        <Dropdown
          id={id}
          name={name}
          label={t('FIELD_DEFAULT_GHG_VALUE_APPLIED')}
          placeholder={t('COMMON_SELECT')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
          options={defaultValueOptions(t)}
        />
      )}
    </Field>
  );
};

/**
 * Props: (none)
 */
export const TotalGhgEmissionsField = () => {
  const t = useTranslations();

  return (
    <Field name="totalGhgEmissions" required>
      {({ id, name, value, onChange, onBlur, status, message, required }) => (
        <TextInput
          id={id}
          name={name}
          label={t('FIELD_TOTAL_GHG_EMISSIONS')}
          placeholder={t('FORM_ENTER_EMISSIONS_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
        />
      )}
    </Field>
  );
};

// One text field per breakdown component — same shape, different name/label/placeholder.
const GHG_BREAKDOWN_FIELDS = [
  ['eecEmissions', 'FORM_EEC_EMISSIONS_OPTIONAL', 'FORM_EEC_EMISSIONS_PLACEHOLDER'],
  ['elEmissions', 'FIELD_EL_EMISSIONS', 'FORM_ENTER_EMISSIONS_PLACEHOLDER'],
  ['epEmissions', 'FORM_EP_EMISSIONS_OPTIONAL', 'FORM_EP_EMISSIONS_PLACEHOLDER'],
  ['etdEmissions', 'FORM_ETD_EMISSIONS_OPTIONAL', 'FORM_ETD_EMISSIONS_PLACEHOLDER'],
  ['euEmissions', 'FIELD_EU_EMISSIONS', 'FORM_ENTER_EMISSIONS_PLACEHOLDER'],
  ['escaEmissions', 'FIELD_ESCA_EMISSIONS', 'FORM_ENTER_EMISSIONS_PLACEHOLDER'],
  ['eccsEmissions', 'FIELD_ECCS_EMISSIONS', 'FORM_ENTER_EMISSIONS_PLACEHOLDER'],
  ['eccrEmissions', 'FIELD_ECCR_EMISSIONS', 'FORM_ENTER_EMISSIONS_PLACEHOLDER'],
];

/**
 * Props:
 *   field  one of GHG_BREAKDOWN_FIELDS' names — which breakdown component this renders
 */
export const GhgBreakdownField = ({ field }) => {
  const t = useTranslations();
  const entry = GHG_BREAKDOWN_FIELDS.find(([name]) => name === field);

  return (
    <Field name={entry[0]}>
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={t(entry[1])}
          placeholder={t(entry[2])}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
        />
      )}
    </Field>
  );
};

/**
 * Props: (none)
 */
export const AllocatedHeatField = () => {
  const t = useTranslations();

  return (
    <Field name="allocatedHeat">
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={t('FORM_ALLOCATED_HEAT')}
          placeholder={t('FORM_ALLOCATED_HEAT_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
        />
      )}
    </Field>
  );
};

/**
 * Props: (none)
 */
export const AllocatedElectricityField = () => {
  const t = useTranslations();

  return (
    <Field name="allocatedElectricity">
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={t('FORM_ALLOCATED_ELECTRICITY')}
          placeholder={t('FORM_ALLOCATED_ELECTRICITY_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
        />
      )}
    </Field>
  );
};

/**
 * Props: (none)
 */
export const EscaBonusCapField = () => {
  const t = useTranslations();

  return (
    <Field name="escaBonusCap" required>
      {({ id, name, value, onChange, onBlur, status, message, required }) => (
        <Dropdown
          id={id}
          name={name}
          label={t('FORM_ESCA_BONUS_CAP')}
          placeholder={t('COMMON_SELECT')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
          required={required}
          options={buildEscaBonusCapOptions(t)}
        />
      )}
    </Field>
  );
};

/**
 * Props: (none)
 */
export const Nuts2AreaField = () => {
  const t = useTranslations();

  return (
    <Field name="nuts2Area">
      {({ id, name, value, onChange, onBlur, status, message }) => (
        <TextInput
          id={id}
          name={name}
          label={t('FORM_NUTS2_AREA')}
          placeholder={t('FORM_NUTS2_AREA_PLACEHOLDER')}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          status={status}
          message={message}
        />
      )}
    </Field>
  );
};
